import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import { EInvoiceItem, IrnValDetailsResponse } from './Services'

const roundToTwo = (value: number): number =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100

const formatToTwo = (value: number): string => roundToTwo(value).toFixed(2)

export const totalValue = (item: EInvoiceItem): number => {
  const { taxableAmount, igstRate, cgstRate, sgstRate } = item
  if (igstRate > 0) {
    return roundToTwo(
      Number(taxableAmount) + Number((taxableAmount * igstRate) / 100)
    )
  }
  const cgstAmount = Number((taxableAmount * cgstRate) / 100)
  const sgstAmount = Number((taxableAmount * sgstRate) / 100)
  return roundToTwo(Number(taxableAmount) + cgstAmount + sgstAmount)
}

export const sumTaxableAmount = (items: EInvoiceItem[]): number =>
  roundToTwo(
    items.reduce((sum, item) => Number(sum) + Number(item.taxableAmount), 0)
  )

export const sumTotalValue = (items: EInvoiceItem[]): number =>
  roundToTwo(items.reduce((sum, item) => Number(sum) + totalValue(item), 0))

export const sumCgstValue = (items: EInvoiceItem[]): number =>
  roundToTwo(
    items.reduce(
      (sum, item) =>
        Number(sum) + Number((item.taxableAmount * item.cgstRate) / 100),
      0
    )
  )

export const sumSgstValue = (items: EInvoiceItem[]): number =>
  roundToTwo(
    items.reduce(
      (sum, item) =>
        Number(sum) + Number((item.taxableAmount * item.sgstRate) / 100),
      0
    )
  )

export const sumIgstValue = (items: EInvoiceItem[]): number =>
  roundToTwo(
    items.reduce(
      (sum, item) =>
        Number(sum) + Number((item.taxableAmount * item.igstRate) / 100),
      0
    )
  )

export interface ItemListBoxProps {
  itemList: EInvoiceItem[]
  setItemList: (items: EInvoiceItem[]) => void
  totals?: IrnValDetailsResponse
}

const ItemListBox: React.FC<ItemListBoxProps> = ({ itemList, setItemList, totals }) => {
  const [itemEntries, setItemEntries] = useState<EInvoiceItem[]>([])
  const lastItemListRef = useRef(null)

  useEffect(() => {
    if (itemList && itemList.length > 0 && lastItemListRef.current !== itemList) {
      lastItemListRef.current = itemList
      setItemEntries(itemList)
      setItemList(itemList)
    }
  }, [itemList, setItemList])

  const handleInputChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    const updatedItems = [...itemEntries]
    const updated = { ...updatedItems[index], [name]: value }

    if (name === 'igstRate' && Number(value) > 0) {
      updated.cgstRate = 0
      updated.sgstRate = 0
    } else if ((name === 'cgstRate' || name === 'sgstRate') && Number(value) > 0) {
      updated.igstRate = 0
    }

    if (name === 'unitPrice' || name === 'quantity') {
      const rate = name === 'unitPrice' ? Number(value) : Number(updated.unitPrice)
      const qty = name === 'quantity' ? Number(value) : Number(updated.quantity)
      updated.taxableAmount = roundToTwo(rate * qty)
    }

    updatedItems[index] = updated
    setItemEntries(updatedItems)
    setItemList(updatedItems)
  }

  const numericCellSx = { textAlign: 'right' as const }

  const summaryBoxSx = {
    p: 2,
    height: '100%',
    boxShadow: 'none',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    boxSizing: 'border-box' as const,
  }

  const tableColumns: { label: string; align?: 'right' }[] = [
    { label: '#' },
    { label: 'DESCRIPTION' },
    { label: 'HSN/SAC (6+)' },
    { label: 'QTY / UNIT' },
    { label: 'UNIT PRICE', align: 'right' },
    { label: 'AMOUNT', align: 'right' },
    { label: 'DISCOUNT', align: 'right' },
    { label: 'TAXABLE', align: 'right' },
    { label: 'GST %' },
    { label: 'TOTAL', align: 'right' },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 1 }}>
        <FormatListBulletedIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="h6" fontWeight="bold">
          Line Item Details
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '4%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              {tableColumns.map((col) => (
                <TableCell
                  key={col.label}
                  align={col.align}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    color: 'white',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ borderLeft: '1px solid', borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
            {itemEntries.map((item, index) => {
              const gstPct =
                item.igstRate > 0
                  ? item.igstRate
                  : Number(item.cgstRate) + Number(item.sgstRate)
              return (
                <TableRow
                  key={index}
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.productName || item.productDesc || '—'}</TableCell>
                  <TableCell>{item.hsnCode || '—'}</TableCell>
                  <TableCell>
                    {item.quantity}{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      {item.qtyUnit}
                    </Typography>
                  </TableCell>
                  <TableCell sx={numericCellSx}>
                    {formatToTwo(item.unitPrice)}
                  </TableCell>
                  <TableCell sx={numericCellSx}>
                    {formatToTwo(item.amount ?? 0)}
                  </TableCell>
                  <TableCell sx={numericCellSx}>
                    {formatToTwo(item.discount ?? 0)}
                  </TableCell>
                  <TableCell sx={{ ...numericCellSx, fontWeight: 'bold' }}>
                    {formatToTwo(item.taxableAmount)}
                  </TableCell>
                  <TableCell>{gstPct}%</TableCell>
                  <TableCell sx={numericCellSx}>
                    {formatToTwo(totalValue(item))}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          mt: 2,
          alignItems: 'stretch',
          width: '100%',
        }}
      >
        <Box sx={{ ...summaryBoxSx, flex: 1, minWidth: 0 }}>
           
            {[
              { label: 'Discount', value: totals?.discount },
              { label: 'Other Charges', value: totals?.othChrg },
              { label: 'Cess Amount', value: totals?.cesVal },
              { label: 'State Cess', value: totals?.stCesVal },
              { label: 'Round Off', value: totals?.rndOffAmt },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2">
                  {value != null ? formatToTwo(value) : '0.00'}
                </Typography>
              </Box>
            ))}
        </Box>
        <Box sx={{ ...summaryBoxSx, flex: 1, minWidth: 0 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}
            >
              <Typography variant="body2" color="text.secondary">
                Total Taxable Value
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                ₹{' '}
                {totals?.assVal != null
                  ? formatToTwo(totals.assVal)
                  : formatToTwo(sumTaxableAmount(itemEntries))}
              </Typography>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}
            >
              <Typography variant="body2" color="text.secondary">
                Total Tax Components
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2">
                  CGST: ₹{' '}
                  {totals?.cgstVal != null
                    ? formatToTwo(totals.cgstVal)
                    : formatToTwo(sumCgstValue(itemEntries))}
                </Typography>
                <Typography variant="body2">
                  SGST: ₹{' '}
                  {totals?.sgstVal != null
                    ? formatToTwo(totals.sgstVal)
                    : formatToTwo(sumSgstValue(itemEntries))}
                </Typography>
                <Typography variant="body2">
                  IGST: ₹{' '}
                  {totals?.igstVal != null
                    ? formatToTwo(totals.igstVal)
                    : formatToTwo(sumIgstValue(itemEntries))}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Total Invoice Value
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                ₹{' '}
                {totals?.totInvVal != null
                  ? formatToTwo(totals.totInvVal)
                  : formatToTwo(sumTotalValue(itemEntries))}
              </Typography>
            </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ItemListBox
